"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useTransform, type MotionValue } from "framer-motion";
import { HoldingShape } from "@repo/ui/holding-shape";
import { SolidShape } from "@repo/ui/solid-shape";
import { cn } from "@repo/ui/utils";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { brandColorBgClass } from "@/lib/color-utils";
import type { GridCell } from "./image-shape-grid.types";
const FADE_TRANSITION = {
  duration: 1.2,
  ease: [0.4, 0, 0.2, 1]
} as const;
/**
 * Hover settings per cell type: rest scale, hover scale, and the
 * transition that runs between them. Tuned so the bigger movements
 * get more room and the subtler ones feel snappy.
 *
 * - solid: biggest scale, longest + softest curve (easeOutExpo-ish) so
 *   the dramatic expansion lands without feeling abrupt.
 * - shaped: small scale, short snappy easeOut — a light response.
 * - image: plain images sit pre-zoomed at rest and step back to 1.0
 *   on hover; easeInOut gives equal weight on entry and exit.
 */
const HOVER_BY_TYPE = {
  image: {
    rest: 1.18,
    hover: 1.0,
    transition: {
      duration: 0.6,
      ease: "easeInOut"
    }
  },
  shaped: {
    rest: 1,
    hover: 3.15,
    transition: {
      duration: 2.4,
      ease: [0.16, 1, 0.3, 1]
    }
  },
  solid: {
    rest: 1,
    hover: 3.05,
    transition: {
      duration: 2.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
} as const satisfies Record<GridCell["type"], {
  rest: number;
  hover: number;
  transition: object;
}>;

// The shaped cell's image gets a live inverse of the outer scale via a
// MotionValue (see GridCellComponent), so the two transforms always
// multiply to 1 — independent of the easing curve or duration.

/**
 * Clip-path scale for shaped cells. Keeps the visible holding-shape at
 * roughly its original padded size (15.83% padding ↔ 0.6834 scale)
 * while the underlying box stays at full cell size so the image can
 * cover the cell's full width and height.
 */
const SHAPED_CLIP_SCALE = 0.6834;

// Right-sized, square-cropped, eager — so the cells decode up front instead of
// during scroll (the scroll-time decode burst caused the VAW-1064 flicker).
const CELL_IMAGE_PROPS = {
  sizes: "(max-width: 768px) 50vw, 33vw",
  cropAspect: 1,
  loading: "eager"
} as const;
function renderCellInner(cell: GridCell, innerScale: MotionValue<number>): ReactNode {
  switch (cell.type) {
    case "image":
      return <VisualWrapper visual={cell.visual} className="h-full w-full" visualClassName="h-full w-full" imgClassName="object-cover" extraImageProps={CELL_IMAGE_PROPS} />;
    case "shaped":
      return <div className={cn("h-full w-full", brandColorBgClass[cell.bgColor])}>
          <HoldingShape variant={cell.shape} scale={SHAPED_CLIP_SCALE} className="h-full w-full">
            <motion.div className="h-full w-full" style={{
            scale: innerScale
          }}>
              <VisualWrapper visual={cell.visual} className="h-full w-full" visualClassName="h-full w-full" imgClassName="object-cover" extraImageProps={CELL_IMAGE_PROPS} />
            </motion.div>
          </HoldingShape>
        </div>;
    case "solid":
      return <div className={cn("flex h-full w-full items-center justify-center p-[15.83%]", brandColorBgClass[cell.bgColor])}>
          <SolidShape variant={cell.shape} color={cell.shapeColor} animated={false} className="w-full" />
        </div>;
  }
}
export function GridCellComponent(t0) {
  const $ = _c(20);
  const {
    cell,
    animateKey
  } = t0;
  const [hovered, setHovered] = useState(false);
  const {
    rest,
    hover,
    transition
  } = HOVER_BY_TYPE[cell.type];
  const outerScale = useMotionValue(rest);
  const innerScale = useTransform(outerScale, _temp);
  let t1;
  let t2;
  if ($[0] !== hover || $[1] !== hovered || $[2] !== outerScale || $[3] !== rest || $[4] !== transition) {
    t1 = () => {
      const controls = animate(outerScale, hovered ? hover : rest, transition);
      return () => controls.stop();
    };
    t2 = [hovered, hover, rest, transition, outerScale];
    $[0] = hover;
    $[1] = hovered;
    $[2] = outerScale;
    $[3] = rest;
    $[4] = transition;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== cell || $[8] !== innerScale) {
    t3 = renderCellInner(cell, innerScale);
    $[7] = cell;
    $[8] = innerScale;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const content = t3;
  let t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setHovered(true);
    t5 = () => setHovered(false);
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[12] !== outerScale) {
    t6 = {
      scale: outerScale
    };
    $[12] = outerScale;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== animateKey || $[15] !== content) {
    t7 = animateKey ? <AnimatePresence mode="sync" initial={false}><motion.div key={animateKey} className="absolute inset-0" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={FADE_TRANSITION}>{content}</motion.div></AnimatePresence> : content;
    $[14] = animateKey;
    $[15] = content;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="relative aspect-square overflow-hidden" onMouseEnter={t4} onMouseLeave={t5}><motion.div className="absolute inset-0" style={t6}>{t7}</motion.div></div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp(v) {
  return v === 0 ? 1 : 1 / v;
}